// What a Shop can change on its promo poster: whether the welcome bonus is shown, and its amount.
// The choice is kept on this device, per kiosk URL, so the next visit starts from it.

export interface PosterBonusOptions {
  /** Show the welcome bonus on the poster. */
  showBonus: boolean;
  /** The amount as typed, e.g. "10" or "12.50". */
  amount: string;
}

export const DEFAULT_POSTER_BONUS_OPTIONS: PosterBonusOptions = { showBonus: true, amount: '10' };

/** Largest amount the poster has room for. */
export const MAX_POSTER_BONUS_AMOUNT = 9999;

const STORAGE_PREFIX = 'shop-poster-bonus:';

/**
 * The typed amount as dollars, or null when it cannot go on a poster: empty, not a number, zero
 * or less, more than two decimals, or above MAX_POSTER_BONUS_AMOUNT.
 */
export function parsePosterBonusAmount(raw: string): number | null {
  const text = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const amount = Number(text);
  if (!Number.isFinite(amount) || amount <= 0 || amount > MAX_POSTER_BONUS_AMOUNT) return null;
  return amount;
}

/** The offer as the poster prints it: "$10 FREE", "$12.50 FREE". */
export function posterBonusLabel(amount: number): string {
  const text = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `$${text} FREE`;
}

export function loadPosterBonusOptions(kioskUrl: string): PosterBonusOptions {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + kioskUrl);
    if (!raw) return DEFAULT_POSTER_BONUS_OPTIONS;
    const saved = JSON.parse(raw) as Partial<PosterBonusOptions> | null;
    if (!saved || typeof saved !== 'object') return DEFAULT_POSTER_BONUS_OPTIONS;
    return {
      showBonus: typeof saved.showBonus === 'boolean' ? saved.showBonus : DEFAULT_POSTER_BONUS_OPTIONS.showBonus,
      // A saved amount that no longer passes is replaced, so the poster never opens broken.
      amount:
        typeof saved.amount === 'string' && parsePosterBonusAmount(saved.amount) !== null
          ? saved.amount
          : DEFAULT_POSTER_BONUS_OPTIONS.amount,
    };
  } catch {
    return DEFAULT_POSTER_BONUS_OPTIONS;
  }
}

export function savePosterBonusOptions(kioskUrl: string, options: PosterBonusOptions): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + kioskUrl, JSON.stringify(options));
  } catch {
    /* storage unavailable (private mode): the choice lasts for this visit only */
  }
}
